import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const AdminNavigation = ({ className = '' }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();

  const navigationItems = [
    {
      label: 'Dashboard Overview',
      path: '/admin-dashboard',
      icon: 'LayoutDashboard',
      description: 'Client metrics and system overview'
    },
    {
      label: 'Client Management',
      path: '/admin-dashboard/clients',
      icon: 'Users',
      description: 'Manage client accounts and progress'
    },
    {
      label: 'Curriculum Content',
      path: '/admin-dashboard/curriculum',
      icon: 'BookOpen',
      description: 'Daily readings and content management'
    },
    {
      label: 'Analytics & Reports',
      path: '/admin-dashboard/analytics',
      icon: 'BarChart3',
      description: 'Detailed progress analytics and reports'
    },
    {
      label: 'System Settings',
      path: '/admin-dashboard/settings',
      icon: 'Settings',
      description: 'Application configuration and preferences'
    }
  ];

  const isActive = (path) => {
    if (path === '/admin-dashboard') {
      return location?.pathname === path;
    }
    return location?.pathname?.startsWith(path);
  };

  return (
    <>
      {/* Desktop Sidebar Navigation */}
      <nav className={`hidden lg:block fixed left-0 top-16 bottom-0 bg-card border-r border-border z-90 transition-all duration-300 animate-spring ${
        isCollapsed ? 'w-16' : 'w-64'
      } ${className}`}>
        {/* Collapse Toggle */}
        <div className="flex items-center justify-end p-4 border-b border-border">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon 
              name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} 
              size={16} 
              className="text-muted-foreground"
            />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="p-4 space-y-2">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 animate-spring group ${
                isActive(item?.path)
                  ? 'text-primary bg-primary/10 shadow-gentle'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted hover:shadow-gentle-hover'
              }`}
              title={isCollapsed ? item?.label : item?.description}
            >
              <Icon 
                name={item?.icon} 
                size={20} 
                className={`flex-shrink-0 transition-colors duration-200 ${
                  isActive(item?.path) ? 'text-primary' : 'text-current'
                }`}
              />
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className={`font-caption font-medium transition-colors duration-200 ${
                    isActive(item?.path) ? 'text-primary' : 'text-current'
                  }`}>
                    {item?.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {item?.description}
                  </p>
                </div>
              )}
            </Link>
          ))}
        </div>
      </nav>
      {/* Mobile Card-based Navigation */}
      <div className="lg:hidden p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-start space-x-3 p-4 rounded-lg border transition-all duration-200 animate-spring ${
                isActive(item?.path)
                  ? 'text-primary bg-primary/10 border-primary/20 shadow-gentle'
                  : 'text-foreground bg-card border-border hover:bg-muted hover:shadow-gentle-hover'
              }`}
            >
              <Icon 
                name={item?.icon} 
                size={24} 
                className={`flex-shrink-0 mt-0.5 transition-colors duration-200 ${
                  isActive(item?.path) ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
              <div className="flex-1 min-w-0">
                <h3 className={`font-caption font-semibold transition-colors duration-200 ${
                  isActive(item?.path) ? 'text-primary' : 'text-foreground'
                }`}>
                  {item?.label}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {item?.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};

export default AdminNavigation;